import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, TrendingDown, Info } from "lucide-react";
import { journeyApi } from "@/lib/journeyApi";
import { fmt } from "@/lib/format";
import { track } from "@/lib/analytics";

// Deterministic fixed-rate Mortgage Freedom payoff scenario. Educational and
// planning-only: nothing is paid until the customer confirms a Mortgage Sprint.
export function MortgageScenario({ compact }) {
  const [base, setBase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extra, setExtra] = useState("");
  const [accel, setAccel] = useState(null);
  const [calcing, setCalcing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    journeyApi.mortgageScenario()
      .then((d) => { if (!cancelled) { setBase(d); if (d?.has_scenario) track("mortgage_scenario_viewed"); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const n = parseFloat(extra);
    if (!Number.isFinite(n) || n <= 0) { setAccel(null); return; }
    let cancelled = false;
    setCalcing(true);
    const t = setTimeout(() => {
      journeyApi.mortgageScenario(n, 0)
        .then((d) => { if (!cancelled) setAccel(d?.accelerated || null); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setCalcing(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [extra]);

  if (loading) return <div className="mt-4 h-28 animate-pulse rounded-2xl bg-[#F0EBE2]" data-testid="mortgage-scenario-loading" />;
  if (!base?.has_mortgage) return null;

  if (!base.has_scenario) {
    return (
      <div className="mt-4 rounded-2xl pe-surface p-5" data-testid="mortgage-scenario-incomplete">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]"><Home className="h-4 w-4" /> Mortgage Freedom</p>
        <p className="mt-2 text-sm text-[#70584A]">{base.reason}</p>
        <Link to="/dashboard/foundation/essentials?from=goal" data-testid="mortgage-scenario-edit" className="mt-2 inline-block text-sm font-medium text-[#2F6B57] underline underline-offset-4 hover:text-[#164C3B]">Add your mortgage details</Link>
      </div>
    );
  }

  const b = base.base;
  return (
    <div className="mt-4 rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5" data-testid="mortgage-scenario">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]"><Home className="h-4 w-4" /> Mortgage Freedom scenario</p>
      <p className="mt-2 text-xs leading-relaxed text-[#8A7C6B]" data-testid="mortgage-illustrative-note">
        Explore how different payment assumptions could affect your estimated mortgage payoff timeline. Your lender's actual terms and calculations control.
      </p>
      <p className="mt-2 text-sm text-[#70584A]">
        At {fmt(base.monthly_payment)}/mo{base.interest_rate ? ` and ${base.interest_rate}%` : ""}, you're on track to be mortgage-free in{" "}
        <span className="font-semibold text-[#164C3B]" data-testid="mortgage-base-payoff">{b.payoff_label}</span>,
        paying <span className="font-semibold text-[#164C3B]">{fmt(b.total_interest)}</span> in interest.
      </p>

      {base.inconsistent && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#C9A86A]/50 bg-white p-3" data-testid="mortgage-inconsistent">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#9A7B3A]" />
          <p className="text-xs leading-relaxed text-[#70584A]">
            Heads up: the monthly payment and the remaining term you entered ({base.entered_term_label}) don't line up.
            We didn't change anything. This scenario uses your <span className="font-medium text-[#164C3B]">monthly payment</span>
            {base.interest_rate ? " and rate" : ""}, which pays off in <span className="font-medium text-[#164C3B]">{b.payoff_label}</span>.
            If that looks off, double-check your balance, rate, or payment.
          </p>
        </div>
      )}

      <div className="mt-4 rounded-xl pe-surface p-4">
        <label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">What if you added extra each month?</label>
        <div className="relative mt-1 max-w-[220px]">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#A89A88]">$</span>
          <input inputMode="decimal" value={extra} onChange={(e) => setExtra(e.target.value.replace(/[^0-9.]/g, ""))}
            placeholder="e.g. 300" data-testid="mortgage-extra-input"
            className="min-h-11 w-full rounded-xl pe-surface pl-7 pr-3 text-[#171717] outline-none focus:border-[#2F6B57]" />
        </div>
        {calcing && <p className="mt-2 text-xs text-[#A89A88]">Calculating…</p>}
        {accel && !calcing && (
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm" data-testid="mortgage-accel">
            <div className="rounded-xl bg-[#DDE7DF] p-3">
              <p className="flex items-center gap-1 text-xs text-[#2F6B57]"><TrendingDown className="h-3.5 w-3.5" /> Mortgage-free sooner</p>
              <p className="mt-1 font-serif text-xl text-[#164C3B]" data-testid="mortgage-months-saved">{accel.months_saved_label}</p>
            </div>
            <div className="rounded-xl bg-[#DDE7DF] p-3">
              <p className="text-xs text-[#2F6B57]">Interest saved</p>
              <p className="mt-1 font-serif text-xl text-[#164C3B]" data-testid="mortgage-interest-saved">{fmt(accel.interest_saved)}</p>
            </div>
          </div>
        )}
      </div>

      {!compact && (
        <p className="mt-3 flex items-start gap-1.5 text-xs leading-relaxed text-[#8A7C6B]">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          An educational fixed-rate estimate based on the figures you entered. It assumes your rate and payment stay the
          same. The Paycheck Edit does not refinance, move money, or guarantee outcomes. You confirm every Mortgage Edit.
        </p>
      )}
    </div>
  );
}
